// RO_CIUS (UBL 2.1) e-Factura XML Generator

interface XmlInvoiceItem {
  item_name: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
  unit_code: string;
}

interface XmlInvoiceData {
  invoice_number: string | null;
  issue_date: string;
  due_date: string | null;
  seller_name: string | null;
  seller_cui: string | null;
  seller_address: string | null;
  seller_city: string | null;
  seller_county: string | null;
  buyer_name: string | null;
  buyer_cui: string | null;
  buyer_address: string | null;
  buyer_city: string | null;
  buyer_county: string | null;
  subtotal: number;
  vat_total: number;
  total: number;
  items: XmlInvoiceItem[];
}

const esc = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");

const formatCui = (cui: string | null): string => {
  if (!cui) return "";
  return cui.replace(/^RO/i, "").trim();
};

const vatCategoryCode = (rate: number): string => {
  if (rate === 0) return "Z"; // Zero-rated
  if (rate > 0) return "S"; // Standard
  return "O"; // Not subject
};

export function validateInvoiceForXml(data: XmlInvoiceData): string[] {
  const errors: string[] = [];
  if (!data.invoice_number) errors.push("Numărul facturii este obligatoriu");
  if (!data.issue_date) errors.push("Data emiterii este obligatorie");
  if (!data.seller_name) errors.push("Numele furnizorului este obligatoriu");
  if (!data.seller_cui) errors.push("CUI-ul furnizorului este obligatoriu");
  if (!data.seller_city) errors.push("Orașul furnizorului este obligatoriu");
  if (!data.seller_county) errors.push("Județul furnizorului este obligatoriu");
  if (!data.buyer_name) errors.push("Numele clientului este obligatoriu");
  if (!data.buyer_cui) errors.push("CUI-ul clientului este obligatoriu");
  if (!data.buyer_city) errors.push("Orașul clientului este obligatoriu");
  if (!data.buyer_county) errors.push("Județul clientului este obligatoriu");
  if (!data.items.length) errors.push("Factura trebuie să aibă cel puțin un produs");
  return errors;
}

export function generateInvoiceXml(data: XmlInvoiceData): string {
  const cuiSeller = formatCui(data.seller_cui);
  const cuiBuyer = formatCui(data.buyer_cui);

  // Group items by VAT rate for TaxTotal breakdown
  const vatGroups: Record<number, { taxable: number; tax: number }> = {};
  data.items.forEach((item) => {
    const lineNet = item.quantity * item.unit_price;
    const lineTax = lineNet * (item.vat_rate / 100);
    if (!vatGroups[item.vat_rate]) vatGroups[item.vat_rate] = { taxable: 0, tax: 0 };
    vatGroups[item.vat_rate].taxable += lineNet;
    vatGroups[item.vat_rate].tax += lineTax;
  });

  const taxSubtotals = Object.entries(vatGroups)
    .map(
      ([rate, g]) => `
      <cac:TaxSubtotal>
        <cbc:TaxableAmount currencyID="RON">${g.taxable.toFixed(2)}</cbc:TaxableAmount>
        <cbc:TaxAmount currencyID="RON">${g.tax.toFixed(2)}</cbc:TaxAmount>
        <cac:TaxCategory>
          <cbc:ID>${vatCategoryCode(Number(rate))}</cbc:ID>
          <cbc:Percent>${Number(rate).toFixed(2)}</cbc:Percent>
          <cac:TaxScheme>
            <cbc:ID>VAT</cbc:ID>
          </cac:TaxScheme>
        </cac:TaxCategory>
      </cac:TaxSubtotal>`
    )
    .join("");

  const invoiceLines = data.items
    .map(
      (item, idx) => {
        const lineNet = item.quantity * item.unit_price;
        const lineTax = lineNet * (item.vat_rate / 100);
        return `
    <cac:InvoiceLine>
      <cbc:ID>${idx + 1}</cbc:ID>
      <cbc:InvoicedQuantity unitCode="${esc(item.unit_code)}">${item.quantity}</cbc:InvoicedQuantity>
      <cbc:LineExtensionAmount currencyID="RON">${lineNet.toFixed(2)}</cbc:LineExtensionAmount>
      <cac:Item>
        <cbc:Name>${esc(item.item_name)}</cbc:Name>
        <cac:ClassifiedTaxCategory>
          <cbc:ID>${vatCategoryCode(item.vat_rate)}</cbc:ID>
          <cbc:Percent>${item.vat_rate.toFixed(2)}</cbc:Percent>
          <cac:TaxScheme>
            <cbc:ID>VAT</cbc:ID>
          </cac:TaxScheme>
        </cac:ClassifiedTaxCategory>
      </cac:Item>
      <cac:Price>
        <cbc:PriceAmount currencyID="RON">${item.unit_price.toFixed(2)}</cbc:PriceAmount>
      </cac:Price>
    </cac:InvoiceLine>`;
      }
    )
    .join("");

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
  xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
  xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
  <cbc:CustomizationID>urn:cen.eu:en16931:2017#compliant#urn:efactura.mfinante.ro:CIUS-RO:1.0.1</cbc:CustomizationID>
  <cbc:ID>${esc(data.invoice_number || "")}</cbc:ID>
  <cbc:IssueDate>${data.issue_date}</cbc:IssueDate>${data.due_date ? `
  <cbc:DueDate>${data.due_date}</cbc:DueDate>` : ""}
  <cbc:InvoiceTypeCode>380</cbc:InvoiceTypeCode>
  <cbc:DocumentCurrencyCode>RON</cbc:DocumentCurrencyCode>

  <cac:AccountingSupplierParty>
    <cac:Party>
      <cac:PartyName>
        <cbc:Name>${esc(data.seller_name || "")}</cbc:Name>
      </cac:PartyName>
      <cac:PostalAddress>
        <cbc:StreetName>${esc(data.seller_address || "")}</cbc:StreetName>
        <cbc:CityName>${esc(data.seller_city || "")}</cbc:CityName>
        <cbc:CountrySubentity>${esc(data.seller_county || "")}</cbc:CountrySubentity>
        <cac:Country>
          <cbc:IdentificationCode>RO</cbc:IdentificationCode>
        </cac:Country>
      </cac:PostalAddress>
      <cac:PartyTaxScheme>
        <cbc:CompanyID>RO${cuiSeller}</cbc:CompanyID>
        <cac:TaxScheme>
          <cbc:ID>VAT</cbc:ID>
        </cac:TaxScheme>
      </cac:PartyTaxScheme>
      <cac:PartyLegalEntity>
        <cbc:RegistrationName>${esc(data.seller_name || "")}</cbc:RegistrationName>
        <cbc:CompanyID>${cuiSeller}</cbc:CompanyID>
      </cac:PartyLegalEntity>
    </cac:Party>
  </cac:AccountingSupplierParty>

  <cac:AccountingCustomerParty>
    <cac:Party>
      <cac:PartyName>
        <cbc:Name>${esc(data.buyer_name || "")}</cbc:Name>
      </cac:PartyName>
      <cac:PostalAddress>
        <cbc:StreetName>${esc(data.buyer_address || "")}</cbc:StreetName>
        <cbc:CityName>${esc(data.buyer_city || "")}</cbc:CityName>
        <cbc:CountrySubentity>${esc(data.buyer_county || "")}</cbc:CountrySubentity>
        <cac:Country>
          <cbc:IdentificationCode>RO</cbc:IdentificationCode>
        </cac:Country>
      </cac:PostalAddress>
      <cac:PartyTaxScheme>
        <cbc:CompanyID>RO${cuiBuyer}</cbc:CompanyID>
        <cac:TaxScheme>
          <cbc:ID>VAT</cbc:ID>
        </cac:TaxScheme>
      </cac:PartyTaxScheme>
      <cac:PartyLegalEntity>
        <cbc:RegistrationName>${esc(data.buyer_name || "")}</cbc:RegistrationName>
        <cbc:CompanyID>${cuiBuyer}</cbc:CompanyID>
      </cac:PartyLegalEntity>
    </cac:Party>
  </cac:AccountingCustomerParty>

  <cac:PaymentMeans>
    <cbc:PaymentMeansCode>42</cbc:PaymentMeansCode>
  </cac:PaymentMeans>

  <cac:TaxTotal>
    <cbc:TaxAmount currencyID="RON">${data.vat_total.toFixed(2)}</cbc:TaxAmount>${taxSubtotals}
  </cac:TaxTotal>

  <cac:LegalMonetaryTotal>
    <cbc:LineExtensionAmount currencyID="RON">${data.subtotal.toFixed(2)}</cbc:LineExtensionAmount>
    <cbc:TaxExclusiveAmount currencyID="RON">${data.subtotal.toFixed(2)}</cbc:TaxExclusiveAmount>
    <cbc:TaxInclusiveAmount currencyID="RON">${data.total.toFixed(2)}</cbc:TaxInclusiveAmount>
    <cbc:PayableAmount currencyID="RON">${data.total.toFixed(2)}</cbc:PayableAmount>
  </cac:LegalMonetaryTotal>
${invoiceLines}
</Invoice>`;

  return xml;
}

export function downloadXml(xml: string, filename: string) {
  const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
